/**
 * Adaptive Timeline Support
 *
 * Provides consequence-based task triage with progressive disclosure.
 * UX Philosophy: Maximally Assistive, Cognitively Respectful, Strategically Transparent
 */

import React, { useState, useEffect } from 'react';
import { useSessionContext } from '../../../contexts/SessionContext';
import AdaptiveTimelineSupportService from '../../../services/AdaptiveTimelineSupportService';
import FocusSelector from './FocusSelector';
import MustCompleteSection from './MustCompleteSection';
import SupportAccordion from './SupportAccordion';
import QualityMattersSection from './QualityMattersSection';
import SafeToSimplifySection from './SafeToSimplifySection';
import SeasonalContextSection from './SeasonalContextSection';
import { Zap } from 'lucide-react';

const AdaptiveTimelineSupport = ({ timeline, currentDate, currentUser, onApplyPlan }) => {
  const { startSession } = useSessionContext();
  const [selectedCategory, setSelectedCategory] = useState('all');
  const [supportData, setSupportData] = useState(null);
  const [isExpanded, setIsExpanded] = useState(false);
  const [loading, setLoading] = useState(false);

  // Generate support plan when category changes
  useEffect(() => {
    if (!timeline || timeline.length === 0) return;

    setLoading(true);
    try {
      const plan = AdaptiveTimelineSupportService.generateFocusedSupport(
        timeline,
        selectedCategory,
        currentDate
      );

      setSupportData(plan);

      // Auto-expand if "all" category (everything feels overwhelming)
      setIsExpanded(selectedCategory === 'all');
    } catch (error) {
      console.error('Error generating support plan:', error);
    } finally {
      setLoading(false);
    }
  }, [timeline, selectedCategory, currentDate]);

  // Handler to start catch-up session with must-complete tasks
  const handleStartCatchUpSession = () => {
    if (!supportData || !supportData.categorized) return;

    const tasks = supportData.categorized.mustComplete.slice(0, 3);
    if (tasks.length === 0) return;

    const totalMinutes = tasks.reduce((sum, t) => sum + (t.estimated_hours || 1) * 60, 0);

    startSession({
      id: `catchup-${Date.now()}`,
      title: '🎯 Catch-Up Session',
      batchType: 'CATCH_UP',
      tasks: tasks.map(task => {
        const sessionTask = {
          id: task.id,
          title: task.title || task.task_name,
          isCompleted: false
        };

        // Only add module_link if it exists and is truthy
        const link = task.college_journey_link || task.module_link;
        if (link) {
          sessionTask.module_link = link;
        }

        // Only add college_id if it exists
        if (task.college_id) {
          sessionTask.college_id = task.college_id;
        }

        return sessionTask;
      }),
      estimatedMinutes: totalMinutes
    });
  };

  if (loading || !supportData) {
    return (
      <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl p-6 border-2 border-slate-200">
        <div className="flex items-center gap-2 text-slate-600">
          <Zap className="w-5 h-5 animate-pulse" />
          <span className="text-sm font-medium">Analyzing your timeline...</span>
        </div>
      </div>
    );
  }

  // Empty state - all caught up!
  if (supportData.isEmpty) {
    return (
      <div className="bg-gradient-to-br from-emerald-50 via-teal-50 to-cyan-50 rounded-xl p-6 border-2 border-emerald-200">
        <div className="flex items-center gap-3 mb-2">
          <div className="w-10 h-10 bg-emerald-500 rounded-full flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <h3 className="text-lg font-bold text-slate-900">You're on track!</h3>
        </div>
        <p className="text-sm text-slate-700">{supportData.message}</p>
      </div>
    );
  }

  const { categorized, metrics } = supportData;
  const showSeasonalContext = selectedCategory === 'all' && isExpanded;

  return (
    <div className="bg-gradient-to-br from-indigo-50 via-purple-50 to-pink-50 rounded-xl p-6 border-2 border-slate-200 space-y-4">
      {/* Header */}
      <div className="flex items-start justify-between gap-4">
        <div className="flex items-center gap-3">
          <div className="w-10 h-10 bg-gradient-to-br from-indigo-500 to-purple-500 rounded-full flex items-center justify-center">
            <Zap className="w-5 h-5 text-white" />
          </div>
          <div>
            <h3 className="text-lg font-bold text-slate-900">Timeline Support</h3>
            <p className="text-xs text-slate-600">Get focused help with what matters most</p>
          </div>
        </div>
      </div>

      {/* Focus Selector */}
      <FocusSelector
        selectedCategory={selectedCategory}
        onCategoryChange={setSelectedCategory}
      />

      {/* Must Complete Section - Always Visible */}
      {categorized.mustComplete.length > 0 && (
        <MustCompleteSection
          tasks={categorized.mustComplete.slice(0, 3)} // Max 3 for focus
          onStartSession={handleStartCatchUpSession}
        />
      )}

      {/* Progressive Disclosure - Accordion */}
      {(categorized.qualityMatters.length > 0 || categorized.safeToSimplify.length > 0) && (
        <SupportAccordion
          isExpanded={isExpanded}
          onToggle={() => setIsExpanded(!isExpanded)}
          metrics={metrics}
        >
          {/* Quality Matters Section */}
          {categorized.qualityMatters.length > 0 && (
            <QualityMattersSection
              tasks={categorized.qualityMatters.slice(0, 3)} // Max 3
              onTaskClick={(task) => {
                if (task.college_journey_link) {
                  window.location.href = task.college_journey_link;
                }
              }}
            />
          )}

          {/* Safe to Simplify Section */}
          {categorized.safeToSimplify.length > 0 && (
            <SafeToSimplifySection
              tasks={categorized.safeToSimplify}
              totalSavings={metrics.totalTimeSavings}
              onApplyMVV={(tasks) => {
                if (onApplyPlan) {
                  onApplyPlan({ type: 'apply_mvv', tasks });
                }
              }}
            />
          )}

          {/* Seasonal Context - Only if "Everything" selected */}
          {showSeasonalContext && (
            <SeasonalContextSection currentDate={currentDate} />
          )}
        </SupportAccordion>
      )}

      {/* No Must Complete Tasks - Start with Quality Matters */}
      {categorized.mustComplete.length === 0 && categorized.qualityMatters.length > 0 && (
        <div className="bg-white/60 rounded-lg p-4 border border-slate-200">
          <p className="text-sm text-slate-700">
            ✨ Great news! No urgent tasks right now. Focus on doing quality work on the tasks above.
          </p>
        </div>
      )}
    </div>
  );
};

export default AdaptiveTimelineSupport;
